import { useMutation } from '@apollo/client'
import { CognitoUser } from '@aws-amplify/auth'
import { Auth } from 'aws-amplify'
import {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import OneSignal from 'react-onesignal'
import {
  handlePayReadyAuthCallback,
  initPayReadyAuth,
  signOutFromPayReady,
  validatePayReadyUserToken,
} from 'src/auth/payready'
import { createLogger } from 'src/logger'
import { LOGOUT_CLEANUP, POST_LOGIN_INIT } from './data/mutations'
import { useDomain } from './DomainProvider'
import PayReadyError from 'src/components/PayReadyError/PayReady'
import { isRunningLocally } from 'src/helpers/runtime'
import { resetLastLocation, TARGET_LOCATION } from 'src/helpers/auth'

const logger = createLogger('AuthProvider')

const AuthContext = createContext<{
  authUser: CognitoUser | null
  signOut: () => void
  isAuthenticated: boolean
  loading: boolean
}>({
  authUser: null,
  signOut: () => {},
  isAuthenticated: false,
  loading: true,
})

export const useAuth = () => useContext(AuthContext)

export const AuthProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [authUser, setUser] = useState<CognitoUser | null>(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [logoutCleanup] = useMutation(LOGOUT_CLEANUP)
  const [postLoginInit] = useMutation(POST_LOGIN_INIT)
  const { isPayReady } = useDomain()

  useEffect(() => {
    ;(async () => {
      // PayReady Auth Flow
      if (isPayReady) {
        try {
          // If the URL contains auth code, then we don't care about existing session
          const urlParams = new URLSearchParams(window.location.search)
          const code = urlParams.get('code')

          if (code) {
            try {
              await handlePayReadyAuthCallback(code)
            } catch (error: any) {
              //If we fail here, then we "stop" and present the error to the user
              //  We don't want to recurse into auth flow as we will get an infinite loop
              //  This will present user with blue "Something went wrong" ... message
              setError(error as any)
              return
            }
          }

          const response = await validatePayReadyUserToken()
          if (response.error || !response.user) {
            //If we did not have the authentication token, then we need to initiate the auth flow
            if (response.error.includes('authentication token')) {
              throw new Error('Not authenticated yet.')
            }
            //BUT, If we fail here for any other reason, then we "stop" and present the error to the user
            //  We don't want to recurse into auth flow as we will get an infinite loop
            setError(response.error)
            return
          }

          // create a mock cognito user
          const mockCognitoUser = {
            getUsername: () => response.user.email,
          } as unknown as CognitoUser

          setUser(mockCognitoUser)
        } catch (error: any) {
          //If we fail here, then we initiate auth flow which should get us the code/token
          console.debug(
            '%c 🔐 Auth flow error (Initiating auth next); Error:',
            'color: red',
            error,
          )
          //For local development, we want to delay the auth flow to make it easier to see the error
          if (isRunningLocally()) {
            //delay for 4 seconds
            await new Promise((resolve) => setTimeout(resolve, 4000))
          }
          initPayReadyAuth()
          return
        }
      }
      // Buzz Auth Flow
      else {
        try {
          const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser()
          setUser(cognitoUser)
        } catch (error: any) {
          setUser(null)
          logger.error({ msg: 'Error authenticating token user', error })
        }
      }

      try {
        // post login
        await postLoginInit()
      } catch (error: any) {
        logger.error({ msg: 'Error posting login init', error })
      } finally {
        setLoading(false)
      }
    })()
  }, [isPayReady, postLoginInit, setUser])

  // sign out
  const signOut = useCallback(async () => {
    try {
      logger.info('Signing out')

      if (!isRunningLocally()) {
        OneSignal.User.removeAlias('externalId')
      }

      //When we logout, we "forget" the last location
      resetLastLocation()
      resetLastLocation(TARGET_LOCATION)

      // make cleanup call to api before logout (e.g mark twillio worker as offline)
      await logoutCleanup()
        // allow logout cleanup to fail silently
        .catch((error) =>
          logger.error({ msg: 'Error cleaning up logout', error }),
        )

      if (isPayReady) {
        signOutFromPayReady()
      } else {
        await Auth.signOut()
      }
    } catch (error) {
      logger.error({ msg: 'Error signing out', error })
    } finally {
      setUser(null)
    }
  }, [logoutCleanup, isPayReady])

  const isAuthenticated = useMemo(
    () => !!authUser && !loading,
    [authUser, loading],
  )

  const values = useMemo(
    () => ({
      authUser,
      signOut,
      isAuthenticated,
      loading,
      error,
    }),
    [isAuthenticated, loading, signOut, authUser, error],
  )

  if (error) {
    return (
      <PayReadyError
        error={error}
        buttonOnClick={() => initPayReadyAuth()}
        buttonText="Retry Login"
      />
    )
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}
